import {IconButton} from 'platform/components';
import {Box, Center, Grid, HStack, Image, Show, Text, useElementSize} from 'platform/foundation';
import {Lightbox, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';
import styled from 'styled-components';

import {isNotEmpty} from 'ramda';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {thumbnailUrl as thumbnailPlaceholderUrl} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useListingGallery} from '../hooks/useListingGallery';
import {getThumbnailSlotCount} from '../utils/getThumbnailSlotCount';
import {GalleryTile} from './GalleryTile';

/** Dark enough for the white count to stay readable on any photo. */
const MORE_TILE_OVERLAY_OPACITY = 0.6;
/** Lets the photo show through the counter pill while the count stays readable. */
const COUNTER_BACKGROUND_OPACITY = 0.8;
/**
 * The main photo is never taller than this share of the window, so in a very wide preview the
 * price below it stays on screen; the gallery then narrows and stays centred.
 */
const MAX_PHOTO_HEIGHT = '60vh';

/**
 * Keeps the 4:3 photo within the height limit by limiting the width of the whole gallery; the
 * stack around it centres the narrowed gallery.
 */
const GalleryFrame = styled.div`
  width: 100%;
  max-width: calc(${MAX_PHOTO_HEIGHT} * 4 / 3);
`;

/** One photo wide; the track inside slides to the current photo. */
const SlideViewport = styled.div`
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
`;

interface SlideTrackProps {
  /** Index of the photo the track is moved to. */
  $offset: number;
  /** A jump (wrapping around) moves the track without the slide animation. */
  $isJumping: boolean;
}

// The offset changes with every photo, so it goes to the inline style through attrs - as a rule
// in the template it would generate a new class per photo.
const SlideTrack = styled.div.attrs<SlideTrackProps>(({$offset}) => ({
  style: {transform: `translateX(-${$offset * 100}%)`},
}))<SlideTrackProps>`
  display: flex;
  height: 100%;
  transition: ${({$isJumping}) => ($isJumping ? 'none' : 'transform 300ms ease-in-out')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Slide = styled.div`
  flex: 0 0 100%;
  height: 100%;
`;

interface ListingGalleryProps extends TestIdProps {
  pictureUrls: string[];
  /** Used only when the listing has no gallery. */
  thumbnailUrl: string | Nullish;
}

/**
 * Main photo with previous/next arrows and a counter, and one row of thumbnails under it whose
 * count follows the preview width. Give it a `key` of the listing so a new listing starts on its
 * first photo.
 */
export function ListingGallery(props: ListingGalleryProps) {
  const actionsFactory = useLightboxActionsFactory();
  const [lightboxControls, {onOpen}] = useLightbox('sourcingListingGallery');
  // Measured on the always rendered frame: the thumbnail row itself mounts only with 2+ photos.
  const [frameRef, frameWidth] = useElementSize<HTMLDivElement>();
  const gallery = useListingGallery(props.pictureUrls, props.thumbnailUrl);
  const images = gallery.images;
  const imageCount = images.length;
  const currentIndex = gallery.currentIndex;
  const slideUrls = isNotEmpty(images) ? images : [thumbnailPlaceholderUrl];
  const hasMultipleImages = imageCount > 1;

  const slotCount = getThumbnailSlotCount(frameWidth);
  const hasMoreThanSlots = imageCount > slotCount;
  const visibleThumbnails = hasMoreThanSlots ? images.slice(0, slotCount - 1) : images;
  const hiddenCount = imageCount - visibleThumbnails.length;
  // Only read when there are more photos than slots, so the photo after the last thumbnail exists.
  const moreTileUrl = images[visibleThumbnails.length] ?? thumbnailPlaceholderUrl;

  const lightboxImages = actionsFactory.addShareOnlyActions(
    (isNotEmpty(images) ? images : [thumbnailPlaceholderUrl]).map((url) => ({
      id: url,
      url: url.replace('resize', 'get'),
    }))
  );

  return (
    <HStack justify="center">
      <GalleryFrame ref={frameRef}>
        <Box position="relative" borderRadius="medium" overflow="hidden">
          <GalleryTile
            label={i18n.t('entity.sourcing.vehiclePreview.actions.showAllPhotos', {
              count: imageCount,
            })}
            onActivate={() => onOpen(currentIndex)}
            onPrevious={hasMultipleImages ? () => gallery.showAt(currentIndex - 1) : undefined}
            onNext={hasMultipleImages ? () => gallery.showAt(currentIndex + 1) : undefined}
          >
            <SlideViewport>
              <SlideTrack
                $offset={currentIndex}
                $isJumping={gallery.isWrapping}
                onTransitionEnd={(event) => {
                  // Only the track's own slide; transitions inside the slides bubble up here too.
                  if (event.target === event.currentTarget) {
                    gallery.endSlide();
                  }
                }}
              >
                {slideUrls.map((url, index) => (
                  <Slide key={url}>
                    <Show when={gallery.isMounted(url, index)}>
                      <Image
                        src={url}
                        alt=""
                        width="100%"
                        height="100%"
                        fit="cover"
                        hasSpinner
                        onError={() => gallery.markFailed(url)}
                        data-testid={
                          index === currentIndex
                            ? testIds.vehicles.publishing('advertisement')
                            : undefined
                        }
                      />
                    </Show>
                  </Slide>
                ))}
              </SlideTrack>
            </SlideViewport>
          </GalleryTile>
          <Show when={hasMultipleImages}>
            <Box position="absolute" top={0} bottom={0} left={2}>
              <Center height="100%">
                <IconButton
                  icon="navigation/chevron_left"
                  priority="secondary"
                  aria-label={i18n.t('entity.sourcing.vehiclePreview.actions.previousPhoto')}
                  onClick={() => gallery.showAt(currentIndex - 1)}
                  data-testid={suffixTestId('galleryPrevious', props)}
                />
              </Center>
            </Box>
            <Box position="absolute" top={0} bottom={0} right={2}>
              <Center height="100%">
                <IconButton
                  icon="navigation/chevron_right"
                  priority="secondary"
                  aria-label={i18n.t('entity.sourcing.vehiclePreview.actions.nextPhoto')}
                  onClick={() => gallery.showAt(currentIndex + 1)}
                  data-testid={suffixTestId('galleryNext', props)}
                />
              </Center>
            </Box>
            <Box
              position="absolute"
              right={2}
              bottom={2}
              paddingHorizontal={2}
              paddingVertical={1}
              borderRadius="small"
              backgroundColor="palettes.neutral.900.100"
              opacity={COUNTER_BACKGROUND_OPACITY}
            >
              {/* Announced on change, so screen reader users hear which photo they moved to. */}
              <div aria-live="polite">
                <Text
                  size="xSmall"
                  color="white"
                  alternative
                  data-testid={suffixTestId('galleryCounter', props)}
                >
                  {`${currentIndex + 1} / ${imageCount}`}
                </Text>
              </div>
            </Box>
          </Show>
        </Box>
        <Show when={hasMultipleImages}>
          <Box paddingTop={2}>
            <Grid columns={slotCount} spacing={1}>
              {visibleThumbnails.map((url, index) => (
                <GalleryTile
                  key={url}
                  label={`${index + 1} / ${imageCount}`}
                  isPressed={index === currentIndex}
                  onActivate={() => gallery.select(url)}
                >
                  <Box
                    border="2px solid"
                    borderColor={index === currentIndex ? 'general.accent' : 'general.transparent'}
                    borderRadius="small"
                    overflow="hidden"
                  >
                    <Image
                      src={url}
                      alt=""
                      ratio="4 / 3"
                      width="100%"
                      fit="cover"
                      hasSpinner
                      onError={() => gallery.markFailed(url)}
                      data-testid={testIds.vehicles.publishing(`advertisement-[${index}]`)}
                    />
                  </Box>
                </GalleryTile>
              ))}
              <Show when={hasMoreThanSlots}>
                <GalleryTile
                  label={i18n.t('entity.sourcing.vehiclePreview.actions.showAllPhotos', {
                    count: imageCount,
                  })}
                  onActivate={() => onOpen(visibleThumbnails.length)}
                >
                  {/* The next photo stays visible under a dark overlay with the count. */}
                  <Box position="relative" borderRadius="small" overflow="hidden">
                    <Image
                      src={moreTileUrl}
                      alt=""
                      ratio="4 / 3"
                      width="100%"
                      fit="cover"
                      hasSpinner
                      onError={() => gallery.markFailed(moreTileUrl)}
                    />
                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      bottom={0}
                      left={0}
                      backgroundColor="palettes.neutral.900.100"
                      opacity={MORE_TILE_OVERLAY_OPACITY}
                    />
                    <Box position="absolute" top={0} right={0} bottom={0} left={0}>
                      <Center height="100%">
                        <Text
                          size="large"
                          color="white"
                          alternative
                          data-testid={suffixTestId('galleryMore', props)}
                        >
                          {`+${hiddenCount}`}
                        </Text>
                      </Center>
                    </Box>
                  </Box>
                </GalleryTile>
              </Show>
            </Grid>
          </Box>
        </Show>
        <Lightbox
          data-testid={testIds.vehicles.publishing('advertisement')}
          controls={lightboxControls}
          images={lightboxImages}
        />
      </GalleryFrame>
    </HStack>
  );
}
